// /src/contexts/SupabaseAuthContext.js
import React, { createContext, useState, useEffect, useContext } from "react";
import { supabase } from "../supabaseClient";

const SupabaseAuthContext = createContext();

// Track processing state to prevent duplicate calls
let isProcessingInvites = false;

export function SupabaseAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Process any pending invites for the user using edge function
  const processUserInvites = async (user) => {
    if (!user || !user.email) return;

    // Prevent concurrent processing of invites
    if (isProcessingInvites) {
      return { processed: false, skipped: true };
    }

    try {
      isProcessingInvites = true;

      // Get the current session to include the JWT token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        console.error(
          "[Error] Failed to get session for invite processing:",
          sessionError
        );
        return { processed: false, error: "No valid session" };
      }

      // Call the edge function to process invites
      const { data, error } = await supabase.functions.invoke(
        "process_user_invites",
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (error) {
        console.error("[Error] Edge function error:", error);
        return { processed: false, error: error.message };
      }

      // If we successfully processed invites, refresh the page to show the profile selection
      if (data.processed && data.count > 0) {
        // Small delay to ensure database operations complete
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }

      return data;
    } catch (err) {
      console.error("[Error] Unexpected error processing invites:", err);
      return { processed: false, error: err.message };
    } finally {
      // Reset processing flag when done
      isProcessingInvites = false;
      // Make sure loading is false in all cases
      setLoading(false);
    }
  };

  useEffect(() => {
    let authListener = null;

    try {
      // Listen for auth changes
      authListener = supabase.auth.onAuthStateChange((event, session) => {
        const userFromSession = session?.user ?? null;
        setUser(userFromSession);

        try {
          // Process invites when user signs in
          if (userFromSession) {
            processUserInvites(userFromSession).catch((err) => {
              console.error("[Error] Failed during invite processing:", err);
              setError(err.message);
              setLoading(false);
            });
          } else {
            // Make sure to set loading to false if no user
            setLoading(false);
          }
        } catch (err) {
          console.error("[Error] Failed during invite processing:", err);
          setError(err.message);
          setLoading(false);
        }
      });

      // Fetch current session on mount
      supabase.auth
        .getSession()
        .then(({ data }) => {
          const userFromSession = data.session?.user ?? null;
          setUser(userFromSession);

          if (userFromSession) {
            // Add slight delay before processing invites on initial load
            setTimeout(() => {
              processUserInvites(userFromSession).catch((err) => {
                console.error("[Error] Process invites error:", err);
                setError(err.message);
                setLoading(false);
              });
            }, 1000);
          } else {
            // Make sure to set loading to false if no user
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error("[Error] Failed to get initial session:", err);
          setError(err.message);
          setLoading(false);
        });
    } catch (e) {
      console.error("[Error] Error setting up auth:", e);
      setError(e.message);
      setLoading(false);
    }

    return () => {
      if (authListener && typeof authListener.unsubscribe === "function") {
        authListener.unsubscribe();
      } else if (authListener && authListener.data) {
        // Handle newer Supabase client versions
        const { subscription } = authListener.data;
        if (subscription && typeof subscription.unsubscribe === "function") {
          subscription.unsubscribe();
        }
      }
    };
  }, []);

  return (
    <SupabaseAuthContext.Provider value={{ user, loading, error }}>
      {children}
    </SupabaseAuthContext.Provider>
  );
}

export function useSupabaseAuth() {
  return useContext(SupabaseAuthContext);
}
